import { useNavigate } from 'react-router-dom'
import {
  DeleteOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ShareAltOutlined,
} from '../../components/icons'
import { useToast } from '../../components/ui/ToastProvider'
import { RowActionsMenuPortal, useRowActionsMenu } from '../../components/ui/rowActionsMenu'

export interface RfqRowMenuProps {
  readonly rfqId: string
  /** A human-readable RFQ label for the trigger's `aria-label` and the
   * Share/Delete toast copy — never the raw id (ADR-0095 / UID-302's
   * no-raw-id rule). */
  readonly rfqLabel: string
}

/**
 * Per-row "⋮ more actions" overflow menu for the Home Open-quotes tab
 * (ADR-0095, UID-302). Mirrors `OrderRowMenu`'s structure and a11y pattern — a
 * plain disclosure widget, not an ARIA APG menu — but keeps its own RFQ-specific
 * items (View → `/rfq/{id}`, Share the RFQ URL, RFQ Delete copy). Open/close,
 * dismissal, and the (portaled, overflow-safe) positioning are shared via
 * {@link useRowActionsMenu} so the two menus can't drift.
 *
 * - **View** navigates to `/rfq/{rfqId}`.
 * - **Share** copies the `/rfq/{rfqId}` detail URL to the clipboard.
 * - **Delete** is INERT — there is no delete/archive/discard operation anywhere
 *   on the RFQ contract; shows an informational toast (a disclosed gap, not a bug).
 */
export function RfqRowMenu({ rfqId, rfqLabel }: RfqRowMenuProps) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose } =
    useRowActionsMenu()

  return (
    <div className="row-actions-menu" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="row-actions-menu-trigger"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`More actions for ${rfqLabel}`}
        onClick={(event) => {
          event.stopPropagation()
          toggleOpen()
        }}
      >
        <EllipsisOutlined aria-hidden="true" />
      </button>
      <RowActionsMenuPortal open={open} coords={coords} menuId={menuId} panelRef={panelRef}>
        <button type="button" onClick={runAndClose(() => navigate(`/rfq/${rfqId}`))}>
          <EyeOutlined aria-hidden="true" />
          View
        </button>
        <button
          type="button"
          onClick={runAndClose(() => {
            void copyRfqLink(rfqId, showToast)
          })}
        >
          <ShareAltOutlined aria-hidden="true" />
          Share
        </button>
        <hr className="row-actions-menu-divider" />
        <button
          type="button"
          className="row-actions-menu-item-danger"
          onClick={runAndClose(() =>
            showToast(`Removing “${rfqLabel}” isn’t available yet.`, 'info'),
          )}
        >
          <DeleteOutlined aria-hidden="true" />
          Delete
        </button>
      </RowActionsMenuPortal>
    </div>
  )
}

async function copyRfqLink(
  rfqId: string,
  showToast: (message: string, tone?: 'success' | 'error' | 'info') => void,
) {
  const url = `${window.location.origin}/rfq/${rfqId}`
  try {
    await navigator.clipboard.writeText(url)
    showToast('Link copied to clipboard', 'success')
  } catch {
    showToast("Couldn't copy the link. Try again.", 'error')
  }
}
